import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { SECTION, SECTION_LIST } from '@aytm/app/stats/page/data-quality-report/lib/sections'
import { SECTION_STATE, resolveSectionState } from '@aytm/app/stats/page/data-quality-report/lib/section-state'
import { loadedAtLabel } from '@aytm/app/stats/page/data-quality-report/lib/format'
import { sectionSubtitle } from '@aytm/app/stats/page/data-quality-report/lib/subtitles'

import Banner from '@aytm/app/stats/page/data-quality-report/Banner'
import SectionShell, { PANEL_TIMEOUT } from '@aytm/app/stats/page/data-quality-report/SectionShell'

import PaidViewpointPanel from '@aytm/app/stats/page/data-quality-report/sections/PaidViewpointPanel'
import TrafficSentinel from '@aytm/app/stats/page/data-quality-report/sections/TrafficSentinel'
import DataCentrifugeSummary from '@aytm/app/stats/page/data-quality-report/sections/DataCentrifugeSummary'
import HumanReview from '@aytm/app/stats/page/data-quality-report/sections/HumanReview'
import SurveyPerformance from '@aytm/app/stats/page/data-quality-report/sections/SurveyPerformance'
import SurveyDesign from '@aytm/app/stats/page/data-quality-report/sections/SurveyDesign'

import style from '@aytm/app/stats/page/data-quality-report/styles/index.module.scss'

const TITLES = {
  [SECTION.PAID_VIEWPOINT]: 'PaidViewpoint proprietary panel',
  [SECTION.TRAFFIC_SENTINEL]: 'Traffic Sentinel',
  [SECTION.DATA_CENTRIFUGE]: 'Data Centrifuge',
  [SECTION.HUMAN_REVIEW]: 'Human review',
  [SECTION.SURVEY_PERFORMANCE]: 'Survey performance and feedback',
  [SECTION.SURVEY_DESIGN]: 'Survey Design'
}

const BODIES = {
  [SECTION.PAID_VIEWPOINT]: PaidViewpointPanel,
  [SECTION.TRAFFIC_SENTINEL]: TrafficSentinel,
  [SECTION.DATA_CENTRIFUGE]: DataCentrifugeSummary,
  [SECTION.HUMAN_REVIEW]: HumanReview,
  [SECTION.SURVEY_PERFORMANCE]: SurveyPerformance,
  [SECTION.SURVEY_DESIGN]: SurveyDesign
}

// Sections whose subtitle figures come from the /dqr report, so their copy waits for it.
// PaidViewpoint and Traffic Sentinel don't depend on the report and keep their copy throughout.
const REPORT_DRIVEN = new Set([
  SECTION.DATA_CENTRIFUGE,
  SECTION.HUMAN_REVIEW,
  SECTION.SURVEY_PERFORMANCE,
  SECTION.SURVEY_DESIGN
])

const ICONS = {
  [SECTION.PAID_VIEWPOINT]: (
    <span className={`${style.sectionIcon} ${style.iconPv}`}>
      <i className="icon-aytmfd-paid-viewpoint" />
    </span>
  ),
  [SECTION.TRAFFIC_SENTINEL]: (
    <span className={`${style.sectionIcon} ${style.iconTraffic}`}>
      <i className="icon-aytmfd-traffic-sentinel" />
    </span>
  ),
  [SECTION.DATA_CENTRIFUGE]: (
    <span className={`${style.sectionIcon} ${style.iconDc}`}>
      <i className="icon-aytmfd-data-centrifuge-v2" />
    </span>
  ),
  [SECTION.HUMAN_REVIEW]: (
    <span className={`${style.sectionIcon} ${style.iconHuman}`}>
      <i className="icon-aytmfd-human-review" />
    </span>
  ),
  [SECTION.SURVEY_PERFORMANCE]: (
    <span className={`${style.sectionIcon} ${style.iconPerf}`}>
      <i className="icon-aytmfd-feedback" />
    </span>
  ),
  [SECTION.SURVEY_DESIGN]: (
    <span className={`${style.sectionIcon} ${style.iconDesign}`}>
      <i className="icon-aytmfd-smile-excited" />
    </span>
  )
}

const DataQualityReport = ({ tier: tierProp, fielding: fieldingProp, data: dataProp, dcController }) => {
  // Sections open independently, so the report holds the whole open set: expanding one leaves the
  // others as they are. PaidViewpoint alone is expanded on load.
  const [openSections, setOpenSections] = useState(() => new Set([SECTION.PAID_VIEWPOINT]))

  // Re-render whenever the controller's /dqr report changes (subscribe/notify seam).
  const [, forceRender] = useState(0)
  useEffect(() => dcController?.subscribe?.(() => forceRender(n => n + 1)), [dcController])

  // Live report wins once /dqr returns; mount-time props are the pre-fetch fallback.
  // Per-key merge for data so sections /dqr doesn't own (e.g. Survey Design) keep their prop value.
  const report = dcController?.getReport?.() || null
  // A failed fetch that never committed a report is terminal — it must not read as "still loading",
  // which would leave the data-driven sections greyed on "Loading..." forever. A failure after a
  // prior success keeps the stale report on screen instead.
  const failed = Boolean(dcController?.hasError?.()) && !report
  const loading = !report && !failed
  // A refresh that fails over figures already on screen is neither of those: the report stays
  // readable, so the only thing wrong is that a reader cannot tell current figures from stale ones.
  // It says which load they are from, and offers the same refetch the failed sections do — so the
  // recency it names is a precondition of the notice, not an extra it can do without.
  const loadedAt = dcController?.getReportLoadedAt?.()
  const stale = Boolean(dcController?.hasError?.()) && Boolean(report) && Boolean(loadedAt)
  const retry = () => dcController?.retryFetch?.()
  const tier = report?.tier || tierProp
  const fielding = report?.fielding || fieldingProp
  // Drives the Data Centrifuge subtitle's in-progress vs completed copy: DC runs On-the-Go, so while
  // the survey is still fielding its section shows an in-progress indicator (§3.7).
  const surveyStatus = report?.surveyStatus
  const data = report ? { ...dataProp, ...report.sections } : dataProp
  // The Data Centrifuge subtitle summarises the body below it, and an admin's vector toggle
  // recomputes that body's quarantined figure without touching the report's committed aggregate.
  // Null wherever the engine has no per-respondent rows to derive one from — the aggregate stands.
  const liveQuarantineCount = dcController?.getLiveQuarantineCount?.()

  const toggle = section =>
    setOpenSections(current => {
      const next = new Set(current)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })

  // Publish the Data Centrifuge section's open state to the embedded DC controller so the Quality
  // sidebar panel can gate its Data Centrifuge controls on it. The signal follows the section's own
  // animation rather than leading it: published on the click, the sidebar would empty while the
  // section was still shutting beside it, and the two surfaces would visibly move out of step.
  const dcOpen = openSections.has(SECTION.DATA_CENTRIFUGE)
  useEffect(() => {
    const timer = setTimeout(() => dcController?.setSectionOpen?.(dcOpen), PANEL_TIMEOUT)
    return () => clearTimeout(timer)
  }, [dcController, dcOpen])

  return (
    <div data-testid="stat-dqr" className={style.component}>
      <Banner />

      {stale ? (
        <div data-testid="stat-dqr-stale" className={style.stale}>
          <span data-testid="stat-dqr-stale-message">Showing data as of {loadedAtLabel(loadedAt)}</span>
          <button data-testid="stat-dqr-stale-refresh" className={style.staleRefresh} type="button" onClick={retry}>
            Refresh
          </button>
        </div>
      ) : null}

      {SECTION_LIST.map(section => {
        const fieldingState = resolveSectionState(section, fielding)
        // While /dqr is loading (or has failed outright), the report-driven sections are greyed + locked.
        let subtitleState = fieldingState
        if (REPORT_DRIVEN.has(section)) {
          if (loading) subtitleState = SECTION_STATE.LOADING_DATA
          if (failed) subtitleState = SECTION_STATE.LOAD_FAILED
        }
        // The embedded DC app owns its own loading/empty state and reads nothing from the /dqr report,
        // so its shell stays open-able throughout — during the fetch, after a failed fetch, and while
        // its own layer is still fielding. Only its subtitle waits for the report's figures, which is
        // why the shell and the subtitle are resolved separately. Retry stays reachable on the sibling
        // report-driven sections, and refetching there refreshes the whole report.
        const state = section === SECTION.DATA_CENTRIFUGE ? SECTION_STATE.ACTIVE : subtitleState
        // Only the Data Centrifuge section consumes the embedded DC controller; Survey Performance
        // takes just the term-report jump, which the controller exposes as a dep pass-through.
        let extra = {}
        let subtitleData = data[section]
        if (section === SECTION.DATA_CENTRIFUGE) {
          extra = { dcController }
          if (liveQuarantineCount != null) {
            subtitleData = { ...subtitleData, quarantineCount: liveQuarantineCount }
          }
        }
        if (section === SECTION.SURVEY_PERFORMANCE) {
          extra = { surveyStatus, onOpenTermReport: () => dcController?.openTermReport?.() }
        }

        const Body = BODIES[section]

        return (
          <SectionShell
            key={section}
            section={section}
            icon={ICONS[section]}
            title={TITLES[section]}
            subtitle={sectionSubtitle(section, subtitleState, subtitleData, surveyStatus)}
            state={state}
            expanded={openSections.has(section)}
            onToggle={() => toggle(section)}
            onRetry={retry}
          >
            <Body tier={tier} state={state} data={data[section] || {}} {...extra} />
          </SectionShell>
        )
      })}
    </div>
  )
}

DataQualityReport.displayName = 'DataQualityReport'
DataQualityReport.propTypes = {
  tier: PropTypes.string.isRequired,
  fielding: PropTypes.object.isRequired,
  data: PropTypes.object,
  dcController: PropTypes.object
}
DataQualityReport.defaultProps = { data: {} }
export default DataQualityReport
